import { useCallback, useEffect, useRef, useState } from 'react'

type Subscriber = (pointsBalance?: number) => void

// Module-level subscribers array shared across all hook instances
let subscribers: Subscriber[] = []

/**
 * A lightweight pub/sub hook for code redeemed events.
 *
 * When a code is successfully claimed, call `notifyCodeRedeemed()` to inform
 * all subscribed components (e.g., CodeProgressBar, PointsBalance) to refresh their data.
 *
 * Optionally pass the new points balance from the claim response so that
 * subscribers can update immediately without an additional API call.
 */
function useCodeRedeemedNotifier() {
  const [codeRedeemedEventCount, setCodeRedeemedEventCount] = useState(0)
  const [lastRedeemedPointsBalance, setLastRedeemedPointsBalance] = useState<
    number | null
  >(null)
  const subscriberRef = useRef<Subscriber | null>(null)

  useEffect(() => {
    const subscriber: Subscriber = (pointsBalance) => {
      setCodeRedeemedEventCount((prev) => prev + 1)
      if (pointsBalance != null) {
        setLastRedeemedPointsBalance(pointsBalance)
      }
    }
    subscriberRef.current = subscriber
    subscribers.push(subscriber)

    return () => {
      subscribers = subscribers.filter((s) => s !== subscriber)
    }
  }, [])

  const notifyCodeRedeemed = useCallback((pointsBalance?: number) => {
    subscribers.forEach((subscriber) => subscriber(pointsBalance))
  }, [])

  return {
    codeRedeemedEventCount,
    lastRedeemedPointsBalance,
    notifyCodeRedeemed
  }
}

export default useCodeRedeemedNotifier
